/**
 * purchaseReturnsDataSource.ts — WatermelonDB → PurchaseReturnInput bridge.
 *
 * Purchase returns raised in the range, with supplier names and the total units
 * returned (summed from each return's items).
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Supplier, PurchaseReturn, PurchaseReturnItem } from '@/db/models/Inventory';
import type { PurchaseReturnInput } from './purchaseReturns';

export async function loadPurchaseReturns(
  storeId: string,
  from: Date,
  to: Date,
): Promise<PurchaseReturnInput[]> {
  const returns = await database.get<PurchaseReturn>('purchase_returns').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('created_at', Q.gte(from.getTime())),
    Q.where('created_at', Q.lt(to.getTime())),
  ).fetch();

  // Total units per return.
  const returnIds = returns.map((r) => r.id);
  const items = await fetchInChunks(returnIds, (batch) =>
    database.get<PurchaseReturnItem>('purchase_return_items').query(
      Q.where('purchase_return_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const qtyByReturn = new Map<string, number>();
  for (const it of items) {
    qtyByReturn.set(it.purchaseReturnId, (qtyByReturn.get(it.purchaseReturnId) ?? 0) + it.quantity);
  }

  // Supplier names.
  const supplierIds = [...new Set(returns.map((r) => r.supplierId).filter(Boolean))];
  const suppliers = await fetchInChunks(supplierIds, (batch) =>
    database.get<Supplier>('suppliers').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const supplierName = new Map(suppliers.map((s) => [s.id, s.name?.trim() || 'Supplier']));

  return returns.map((r) => ({
    returnNumber: r.returnNumber,
    date: r.createdAt,
    supplierName: supplierName.get(r.supplierId) ?? 'Supplier',
    reason: r.reason,
    status: r.status,
    value: r.totalAmount,
    quantity: qtyByReturn.get(r.id) ?? 0,
  }));
}
